import type { OtsFileKind } from "../../components/OtsFileTypeIcon";
import type {
  AuditObjectAuditSummary,
  ContainerVolumeDrop,
  Dispatch,
  DriverActivity,
  GeofenceEvent,
  Message,
  ServiceWork,
  Shift,
  Transport,
  TransportAssoc,
} from "@ots-frontend/ots-data";

import type { ActivityFacets } from "./activity-facets";

/** A picture or document attached to one of a ticket's transports. */
export type ActivityAttachment = {
  id: string;
  /** The transport it is attached to; with `objectType`, the target to ask the attachment service about. */
  objectId: string;
  objectType: string;
  name: string;
  kind: OtsFileKind;
  /** Where the file can be viewed. Signed download links come from the attachment service, not from here. */
  url: string;
  description?: string;
  fileName?: string;
  personName?: string;
  createDttm?: number;
  tenantCode?: string;
};

/** How a stop's geofence visit lines up with its ticket. */
export type ActivityGeofenceMatch = {
  state: "matched" | "overridden" | "outside-shift";
  event: GeofenceEvent;
};

/**
 * Turns the flat `/driver-activity/search` rows (person, start shift, activity…, end shift) into
 * drivers → shifts → entries, and carries over the legacy viewer's highlight rules and footer stats.
 */

export type ActivityWarning = NonNullable<DriverActivity["warnings"]>[number];

export type ActivityEntryKind = "load" | "pickup" | "delivery" | "visit" | "dispatch" | "siteWork" | "tankDrop";

/** Which of a ticket's times the Duration column shows; the legacy viewer's duration setting. */
export type ActivityDurationMode = "turnaround" | "onSite" | "travel" | "billable";

/** What a ticket's ID column shows: its resource ID, or its paper ticket number when it has one. */
export type ActivityIdLabel = "resourceId" | "ticketNumber";

/** The ID column's text for a ticket under `idLabel`; falls back to the resource ID. */
export const activityIdText = (entry: Pick<ActivityEntry, "resourceId" | "pickup" | "delivery">, idLabel: ActivityIdLabel = "resourceId"): string | undefined =>
  (idLabel === "ticketNumber" ? entry.pickup?.ticketNumber ?? entry.delivery?.ticketNumber : undefined) ?? entry.resourceId;

export type ActivityMarkerTone = "neutral" | "info" | "success" | "warning" | "danger" | "violet";

/** A small fact about a ticket or one of its stops, shown as an icon with a tooltip. */
export type ActivityMarker = {
  id:
    | "split" | "well-stage" | "restriction" | "alert"
    | "lact" | "scada-off" | "manual" | "qty-locked"
    | "disputed" | "rejected" | "holding" | "dispatch-link" | "compliance" | "coding";
  label: string;
  tone: ActivityMarkerTone;
  count?: number;
};

export type ActivityCellTone = "danger" | "warning" | "success";

export type ActivityColumn =
  | "pickupTicket"
  | "pickupTime"
  | "pickupSite"
  | "deliverySite"
  | "pickupQty"
  | "deliveryTicket"
  | "deliveryQty"
  | "freight"
  | "work"
  | "revenue";

/** Shaped to spread straight into `OtsFlaggedValue`. */
export type ActivityCellFlag = {
  rule: string;
  tone: ActivityCellTone;
  message: string;
};

export type ActivityValidationStep = {
  id: "dispatch" | "field" | "admin" | "business";
  label: string;
  validatedDttm?: number;
  validatorName?: string;
};

export type ActivityEntry = {
  key: string;
  kind: ActivityEntryKind;
  source: DriverActivity;
  personId?: string;
  /** When the entry happened; pickup time for a load. */
  time?: number;
  tta?: TransportAssoc;
  pickup?: Transport;
  delivery?: Transport;
  geofenceEvent?: GeofenceEvent;
  dispatch?: Dispatch;
  serviceWork?: ServiceWork;
  containerVolumeDrop?: ContainerVolumeDrop;
  /** The shift the entry is listed under, when there is one. */
  shift?: Shift;
  /** WATER or OIL. */
  category?: string;
  /** The ticket's primary identifier, as the operator knows it. */
  resourceId?: string;
  /** The ticket's latest audit, when the host has loaded audits. */
  audit?: AuditObjectAuditSummary;
  /** Every picture and document on the ticket, pictures first. */
  attachments: ActivityAttachment[];
  pictures: ActivityAttachment[];
  documents: ActivityAttachment[];
  /** The geofence visit matched to each stop, when there is one. */
  pickupGeofence?: ActivityGeofenceMatch;
  deliveryGeofence?: ActivityGeofenceMatch;
  /** A ticket for service work rather than a haul; WaterGo colors these apart. */
  isServiceTicket: boolean;
  /** When the entry ended, where it has an end: delivery, leaving a site, finishing site work. */
  endTime?: number;
  markers: ActivityMarker[];
  pickupMarkers: ActivityMarker[];
  deliveryMarkers: ActivityMarker[];
  warnings: ActivityWarning[];
  flags: Partial<Record<ActivityColumn, ActivityCellFlag>>;
  validation: ActivityValidationStep[];
  messages: Message[];
  revenue?: number;
  /** The rate models counted towards revenue and cost, after any rate-model filter. */
  rateModels: NonNullable<TransportAssoc["objectCostRateModelList"]>;
  /** Driver and trailer pay; what the legacy viewer calls cost. */
  cost?: number;
  /** What subcontractor rate models charge for the ticket, whichever rate models are counted. */
  subcontractorCost?: number;
  durationMillis?: number;
  durations: Partial<Record<ActivityDurationMode, number>>;
  /** A warning, a flagged cell, or a ticket with only one side. */
  needsAttention: boolean;
};

export type ActivityShift = {
  key: string;
  shift?: Shift;
  entries: ActivityEntry[];
  totals: ActivityTotals;
};

export type ActivityTotals = {
  shifts: number;
  loads: number;
  quantity: number;
  quantityUom?: string;
  revenue: number;
  cost: number;
  mileage: number;
  attention: number;
  workedMillis: number;
};

export type ActivityDriver = {
  key: string;
  personId?: string;
  name: string;
  companyName?: string;
  shifts: ActivityShift[];
  totals: ActivityTotals;
};

export type ActivityHighlightOptions = {
  /** Color the pickup quantity by how closely it agrees with SCADA. */
  markScada?: boolean;
  /** Compare the driver-entered quantity instead of the ticket quantity. */
  useDriverQuantityForScada?: boolean;
  markDemurrage?: boolean;
  /**
   * How far the ticket may sit above SCADA, in percent of the ticket, before it is an error. WaterGo's
   * `scadaHighPercentage` deployment setting; 5 by default.
   */
  scadaHighPercent?: number;
  /** How far the ticket may sit below SCADA before it is flagged; WaterGo's `scadaLowPercentage`, -5 by default. */
  scadaLowPercent?: number;
  /** Count revenue only from these rate models; every rate model when missing. */
  rateModelNames?: ReadonlySet<string>;
};

/** A correction made in place to one side of a ticket. */
export type ActivityTransportEdit = {
  side: "pickup" | "delivery";
  ticketNumber?: string;
  quantity?: number;
};

export type ActivityViewFilter = "all" | "attention" | "unmatched" | "unvalidated";

export type ActivityFilter = {
  query?: string;
  view?: ActivityViewFilter;
  showVisits?: boolean;
  showDispatches?: boolean;
  showSiteWork?: boolean;
  showTankDrops?: boolean;
  /** Narrowing to some drivers, equipment, or companies. */
  facets?: ActivityFacets;
};

export type ActivityStats = {
  drivers: number;
  loads: number;
  quantity: number;
  quantityUom?: string;
  revenue: number;
  cost: number;
  attention: number;
  /** Tickets with a pickup or a delivery, but not both. */
  unmatched: number;
  unvalidated: number;
  scada: { calculated: number; known: number };
  /** Tickets whose latest audit has a blocking finding. */
  auditBlocked: number;
  /** What subcontractors charge for the day's tickets; the legacy footer's "Subs". */
  subcontractor: number;
};

/**
 * The transport fields one side of a ticket's cells read. Rows depend on this rather than the server's
 * `Transport`, so a renamed server field touches the view-model only.
 */
export type ActivityStop = Pick<Transport,
  | "ticketNumber" | "quantityDecimal" | "quantityUnitOfMeasure"
  | "siteName" | "siteCompanyName" | "containerName" | "containerAbbr" | "wellName" | "stage" | "subStage">;

/** The shift fields the Start Shift and End Shift rows read. */
export type ActivityShiftFacts = Pick<Shift,
  | "startDttm" | "endDttm" | "billableStartDttm" | "billableEndDttm" | "preBillableDttm" | "postBillableDttm"
  | "shiftTypeName" | "driverVerified" | "driverDenied" | "holdingRecord"
  | "vehicleName" | "trailerName" | "trailerCompanyName"
  | "startOdometer" | "endOdometer" | "odometerUom" | "loadedMileage" | "unloadedMileage"
  | "startGeofenceEvent" | "endGeofenceEvent" | "cost" | "revenue" | "messages">;
